import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import { VideoPlayerModal } from '../components/VideoPlayerModal';
import LiveTicker from '../components/LiveTicker';
import ContinueWatching from '../components/ContinueWatching';
import usePersonalizedFeed from '../hooks/usePersonalizedFeed';
import {
  HeroCarousel,
  TrendingNowSection,
  LiveNowSection,
  BecauseYouFollowSection,
  HighlightsSection,
  CompetitionsSection,
  EditorialSection,
  TopMatchesSection,
  SuggestedTeamsSection,
  MatchOfTheDayCard,
  Footer,
} from '../components/home';

export default function NewHome() {
  const navigate = useNavigate();
  const {
    heroRef, watchButtonRef, heroSlides,
    liveContent, competitions, highlightsContent,
    loading, matchMinutes, liveScores, showGlow, getTimeUntilKickoff,
    followedTeams, feedIntelligence,
  } = usePersonalizedFeed();

  const [videoModalContent, setVideoModalContent] = useState(null);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-400">Loading...</p>
        </div>
      </div>
    );
  }

  const play = (v) => setVideoModalContent(v);
  const { hasLive, hasFollowed } = feedIntelligence;

  /*
   * RAIL ORDER — User specified:
   * Hero → Live Now / Because You Follow → Continue Watching →
   * Trending Now → Latest Highlights → Top African Matches →
   * Competitions on AfroSports → AfroSports Originals
   *
   * Dynamic: Live Now shows if live matches exist, Because You Follow
   * shows if user follows teams. Both surface directly under hero.
   */
  const buildRails = () => {
    const rails = [];

    // Primary discovery — live urgency or personal relevance
    if (hasLive) rails.push('live');
    if (hasFollowed) rails.push('because-you-follow');

    // Engagement
    rails.push('continue-watching');

    // Discovery & content
    rails.push('trending');
    rails.push('highlights');
    rails.push('top-matches');
    rails.push('competitions');
    rails.push('originals');

    // Onboarding for new users
    if (!hasFollowed) rails.push('suggested-teams');

    return rails;
  };

  const railOrder = buildRails();

  const railComponents = {
    'live': (
      <LiveNowSection
        key="live"
        liveContent={liveContent}
        liveScores={liveScores}
        matchMinutes={matchMinutes}
        navigate={navigate}
        followedTeams={followedTeams}
      />
    ),
    'because-you-follow': (
      <BecauseYouFollowSection key="because-you-follow" navigate={navigate} onPlayVideo={play} />
    ),
    'continue-watching': (
      <ContinueWatching key="continue-watching" />
    ),
    'trending': (
      <TrendingNowSection key="trending" navigate={navigate} onPlayVideo={play} />
    ),
    'highlights': (
      <HighlightsSection key="highlights" highlightsContent={highlightsContent} navigate={navigate} onPlayVideo={play} />
    ),
    'top-matches': (
      <TopMatchesSection key="top-matches" navigate={navigate} onPlayVideo={play} />
    ),
    'competitions': (
      <CompetitionsSection key="competitions" competitions={competitions} navigate={navigate} />
    ),
    'originals': (
      <EditorialSection key="originals" navigate={navigate} onPlayVideo={play} />
    ),
    'suggested-teams': (
      <SuggestedTeamsSection key="suggested-teams" navigate={navigate} />
    ),
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <Navbar />
      <LiveTicker />

      {/* HERO */}
      {heroSlides.length > 0 ? (
        <HeroCarousel
          slides={heroSlides}
          heroRef={heroRef}
          watchButtonRef={watchButtonRef}
          matchMinutes={matchMinutes}
          showGlow={showGlow}
          getTimeUntilKickoff={getTimeUntilKickoff}
          navigate={navigate}
          onPlayVideo={play}
        />
      ) : (
        <section className="relative min-h-[45vh] flex items-center justify-center bg-[#0a0a0a] border-b border-white/5" data-testid="brand-hero">
          <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1574629810360-7efbbe195018?w=1600&q=80')] bg-cover bg-center opacity-10" />
          <div className="relative z-10 text-center max-w-2xl px-6">
            <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4 leading-tight">The Home of African Sport.</h1>
            <p className="text-lg text-gray-400">Live matches. Exclusive competitions. Original programming — all in one place.</p>
          </div>
        </section>
      )}

      {/* MATCH OF THE DAY — daily anchor, sits above all rails */}
      <MatchOfTheDayCard navigate={navigate} />

      {/* CONTENT RAILS — ordered by user specification */}
      <div>
        {railOrder.map(railKey => railComponents[railKey])}
      </div>

      <Footer />

      {videoModalContent && (
        <VideoPlayerModal video={videoModalContent} onClose={() => setVideoModalContent(null)} />
      )}
    </div>
  );
}
